import React, { useState } from 'react';
import PropTypes from 'prop-types';
import RsTableRow from '@Pages/RetroSynthesis/components/RsTable/RsTableRow';
import RsEditableItem from '@Pages/RetroSynthesis/components/RsEditableItem';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import { Modal } from '@els/els-react--modal';
import { Pill } from '@els/els-react--pill';
import { imageTypes } from '@Constants/rx-ui/constants';
import ConditionsReactionImage from './ConditionsReactionImage';
import ConditionsSourceLink from './ConditionsSourceLink';
import { trackNavigatorProjectNameEdit, trackNavigatorZoom, trackNavigatorProjectSource } from '../analytics';
import './ConditionsTableRow.scss';

function ConditionsTableRow({ run, onOpen, selected, onSelectionChange, onRename, onEdit, onBusyChange, onBeforeNavigate }) {
  const [isEditMode, setEditMode] = useState(false);
  const [isZoomOpen, setZoomOpen] = useState(false);

  const handleSetEditMode = (editMode) => {
    if (editMode) {
      trackNavigatorProjectNameEdit();
    }
    setEditMode(editMode);
  };

  const handleZoom = () => {
    trackNavigatorZoom();
    setZoomOpen(true);
  };
  const resultCount = run.resultCount ?? 0;
  const uniqueReactions = run.uniqueReactions ?? 0;
  const hasConditionResults = Number(resultCount) > 0 || Number(uniqueReactions) > 0;

  return (
    <RsTableRow>
      <td className="rx-table-row__select c-els-table__cell c-els-table__basic-cell">
        <Checkbox
          data-e2e={`conditions-select-${run.id}`}
          id={`conditions-select-${run.id}`}
          checked={selected}
          onChange={(e) => onSelectionChange(run.id, e?.currentTarget?.checked)}
          label={`Select ${run.projectName || run.referenceLabel || run.id}`}
          shouldHideLabel
        />
      </td>
      <td className="rx-table-row__name c-els-table__cell c-els-table__basic-cell">
        <RsEditableItem
          value={run.projectName || run.referenceLabel || ''}
          isEditMode={isEditMode}
          setEditMode={handleSetEditMode}
          onValueEdited={(newName) => onRename(run.id, newName)}
          trackNameEdit={false}
        />
      </td>
      <td className="rx-table-row__date c-els-table__cell c-els-table__basic-cell">{run.date}</td>
      <td className="rx-table-row__reference c-els-table__cell c-els-table__basic-cell">
        <ConditionsSourceLink
          run={run}
          showSourcePill
          linkProps={{ inline: true, navigation: true }}
          onBusyChange={onBusyChange}
          onBeforeNavigate={onBeforeNavigate}
          onSourceClick={trackNavigatorProjectSource}
        />
      </td>
      <td className="rx-table-row__reaction c-els-table__cell c-els-table__basic-cell">
        <div className="rx-reaction-schema" role="img" aria-label={`Input reaction for ${run.projectName || run.referenceLabel}`}>
          <div aria-hidden="true" className="o-els-flex-layout o-els-flex-layout--center">
            <ConditionsReactionImage
              reaction={run.reactionString}
              imageType={imageTypes.CONDITIONS_NAVIGATOR_S}
              alt={`Input reaction for ${run.projectName || run.referenceLabel}`}
            />
          </div>
        </div>
        <div className="rx-table-row__reaction__actions">
          <ButtonWithIcon
            data-e2e="rx-reaction-edit"
            className="rx-table-row__reaction__edit"
            sprite="Edit"
            iconRight
            type="link"
            aria-label={`Edit reaction for ${run.projectName || run.referenceLabel}`}
            onClick={() => onEdit(run)}>
            Edit
          </ButtonWithIcon>
          <ButtonWithIcon
            data-e2e="rx-reaction-zoom"
            className="rx-table-row__reaction__zoom"
            sprite="ZoomIn"
            iconRight
            type="link"
            disabled={!run.reactionString}
            aria-label={`Zoom reaction for ${run.projectName || run.referenceLabel}`}
            onClick={handleZoom}>
            Zoom
          </ButtonWithIcon>
        </div>
      </td>
      <td className="rx-table-row__results c-els-table__cell c-els-table__basic-cell">
        <div className="rx-results-summary">
          <div className="rx-results-stat">
            <span className="rx-results-stat__label">Condition sets</span>
            <Pill pillColor="extended-red-0" textColor="text" className="rx-results-stat__count rx-results-stat__count--sets">
              {String(resultCount)}
            </Pill>
          </div>
          <div className="rx-results-stat">
            <span className="rx-results-stat__label">Examples found</span>
            <Pill pillColor="extended-teal-7" textColor="white" className="rx-results-stat__count rx-results-stat__count--reactions">
              {String(uniqueReactions)}
            </Pill>
          </div>
        </div>
        <Button
          data-e2e="e2e-conditions-open"
          id={`conditions-open-${run.id}`}
          type="primary"
          size="small"
          disabled={!hasConditionResults}
          onClick={() => onOpen(run)}
          aria-label={`Open conditions for ${run.projectName || run.referenceLabel}`}>
          Conditions
        </Button>
      </td>
      {isZoomOpen && (
        <Modal
          id={`conditions-zoom-${run.id}`}
          className="rx-reaction-zoom-modal"
          onClose={() => setZoomOpen(false)}
          a11y={{ name: `Input reaction for ${run.projectName || run.referenceLabel}` }}>
          <div className="rx-reaction-zoom-modal__body">
            <ConditionsReactionImage
              reaction={run.reactionString}
              imageType={imageTypes.CONDITIONS_NAVIGATOR_L}
              alt={`Input reaction for ${run.projectName || run.referenceLabel}`}
            />
          </div>
        </Modal>
      )}
    </RsTableRow>
  );
}

ConditionsTableRow.propTypes = {
  run: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    number: PropTypes.number,
    date: PropTypes.string,
    reference: PropTypes.string,
    referenceType: PropTypes.string,
    referenceLabel: PropTypes.string,
    reactionString: PropTypes.string,
    sourceReactionId: PropTypes.string,
    sourceProjectId: PropTypes.string,
    sourceRouteId: PropTypes.string,
    sourceStepId: PropTypes.string,
    resultCount: PropTypes.number,
    uniqueReactions: PropTypes.number,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  onSelectionChange: PropTypes.func.isRequired,
  onBusyChange: PropTypes.func,
  onBeforeNavigate: PropTypes.func,
};

export default ConditionsTableRow;
